<template>
  <div class="my-profile-page d-flex">
    <v-tabs
      v-model="currentTab"
      direction="vertical"
      class="profile-tabs"
    >
      <v-tab
        v-for="tab in profileTabs"
        :key="tab.id"
        :value="tab.id"
      >
        {{ t(tab.translationKey) }}
      </v-tab>
    </v-tabs>

    <v-window
      v-model="currentTab"
      class="flex-grow-1 tab-content"
    >
      <v-window-item
        v-for="tab in profileTabs"
        :key="tab.id + '-content'"
        :value="tab.id"
      >
        <component
          :is="tab.component"
          :user-profile="userProfile"
          :notifications="notifications"
          :all-notifications="allNotifications"
          :shared-users="sharedUsers"
          :countries="countries"
          @goto="handleGoto"
          @reload-profile="getProfile"
          @reload-notifications="getNotifications"
          @reload-shared-users="getSharedUsers"
          @reload-user-profile="getProfile"
          @add-notification="addNotification"
          @remove-notification="removeNotification"
        />
      </v-window-item>
    </v-window>
  </div>
</template>

<script setup lang="ts">
import { shallowRef, markRaw, watch, getCurrentInstance, onMounted, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Notification } from '@/entities/notification'
import ProfilePersonalData from '@/components/profile/ProfilePersonalData.vue'
import ProfileAuthentication from '@/components/profile/ProfileAuthentication.vue'
import ProfileNotification from '@/components/profile/ProfileNotification.vue'
import ProfileDataSharing from '@/components/profile/ProfileDataSharing.vue'
import ProfileAccountManagement from '@/components/profile/ProfileAccountManagement.vue'
import { Country } from '@/entities/country'
import { ProfileService } from '@/services/profile.service'
import { OrderNotificationsService } from '@/services/order-notifications.service'
import NotificationMessageService from '@/services/notificationmessage.service'
import { useMyProfileUiStore } from '@/stores/myProfileUi'
import { useProfileStore } from '@/stores/profile'
import Keycloak from "keycloak-js";
import getLanguageFromDomain, {isSupportedLocale, SupportedLocale} from "@/utils/language";
import {User} from "@/entities/user";

const { t, locale } = useI18n()
const instance = getCurrentInstance()
const keycloak = instance?.appContext.config.globalProperties.$keycloak as Keycloak
const myProfileUiStore = useMyProfileUiStore()
const profileStore = useProfileStore()

const currentTab = computed({
  get: () => myProfileUiStore.currentTab,
  set: (val: string) => { myProfileUiStore.currentTab = val }
})
const userProfile = shallowRef({})
const notifications = shallowRef([])
const sharedUsers = shallowRef<User[]>([])
const allNotifications = shallowRef<Notification[]>([])
const countries = shallowRef<Country[]>([])

const profileService = new ProfileService()
const orderNotificationsService = new OrderNotificationsService()

type NotificationOptions = { silent?: boolean };

interface ProfileTab {
  no: number
  id: string
  translationKey: string
  component: any
}

const profileTabs: ProfileTab[] = [
  {
    no: 0,
    id: 'personal-data',
    translationKey: 'Personal Data',
    component: markRaw(ProfilePersonalData),
  },
  {
    no: 1,
    id: 'authentication',
    translationKey: 'Authentication',
    component: markRaw(ProfileAuthentication),
  },
  {
    no: 2,
    id: 'notification',
    translationKey: 'Notification',
    component: markRaw(ProfileNotification),
  },
  {
    no: 3,
    id: 'data-sharing',
    translationKey: 'Data sharing',
    component: markRaw(ProfileDataSharing),
  },
  {
    no: 4,
    id: 'account-management',
    translationKey: 'Account management',
    component: markRaw(ProfileAccountManagement),
  },
]

const getProfile = async () => {
  if (keycloak?.subject) {
    const profile = await profileService.getProfile(keycloak.subject)
    userProfile.value = profile
    profileStore.setProfile(profile)

    if (profile.locale && locale.value !== profile.locale) {
      let newLocale: SupportedLocale

      if (isSupportedLocale(profile.locale)) {
        newLocale = profile.locale
      } else {
        const fallback = getLanguageFromDomain()
        newLocale = isSupportedLocale(fallback) ? fallback : 'en'
      }

      // i18n locale type is deeply nested and not directly compatible with SupportedLocale,
      // but runtime values are identical. The check ensures safety.
      locale.value = newLocale as unknown as typeof locale.value
    }
  }
}

const getNotifications = async () => {
  if (keycloak?.subject) {
    notifications.value = await profileService.getNotifications(keycloak.subject)
  }
}

const getSharedUsers = async () => {
  if (keycloak?.subject) {
    sharedUsers.value = (await profileService.getSharedUsers()).filter(user => user.email !== userProfile.value.email)
  }
}

const getAllNotifications = async () => {
  allNotifications.value = await orderNotificationsService.getNotifications()
}

const addNotification = (notification: Notification, options: NotificationOptions = {}) => {
  if (!keycloak?.subject) return

  profileService.postNotification(keycloak.subject, notification)
    .then(() => {
      if (!options?.silent) {
        NotificationMessageService.showSuccess(
          t('Added notification for {notificationName}', {
            notificationName: notification.name,
          }) as string
        )
      }
    })
    .catch(() => {
      NotificationMessageService.showError(
        t('Received an error while adding the notification') as string
      )
      getNotifications()
    })
}

const removeNotification = (notification: Notification) => {
  if (!keycloak?.subject) return

  profileService.deleteNotification(keycloak.subject, notification.notificationId)
    .then(() => {
      NotificationMessageService.showInfo(
        t('Removed notification for {notificationName}', {
          notificationName: notification.name,
        }) as string
      )
    })
    .catch(() => {
      NotificationMessageService.showError(
        t('Received an error while removing the notification') as string
      )
      getNotifications()
    })
}

const handleGoto = (id: string | number) => {
  const tab = profileTabs.find(tab => tab.id === id)
  if (tab) currentTab.value = String(tab.id);
}

const getCountries = async () => {
  countries.value = await profileService.getCountries();
};

onMounted(() => {
  getProfile()
  getAllNotifications()
  getSharedUsers()
  getCountries()
})

watch(() => myProfileUiStore.shouldResetTab, (reset) => {
  if (reset) {
    myProfileUiStore.confirmReset()
  }
})
</script>

<style lang="scss" scoped>
.my-profile-page {
  display: flex;
  min-height: auto;

  .profile {
    &-personal-data,
    &-notification,
    &-authentication,
    &-account-management {
      max-width: 25rem;

      .form-title {
        font-size: 2.5rem;
        line-height: 2.75rem;
        letter-spacing: 0.0625rem;
        text-transform: uppercase;
        color: rgb(var(--v-theme-primary));
      }
    }
  }

  &-tabs {
    flex: 0 0 220px;
    border-right: 1px solid var(--v-theme-on-surface-variant);
  }

  .tab-content {
    flex: 1;
    padding-left: 16px;
  }

  @media #{map.get($display-breakpoints, 'sm-and-down')} {
    margin-top: 1rem;

    .v-tabs-items {
      margin-left: 0;
      margin-right: 0;
    }

    .profile-personal-data {
      max-width: none;
    }
  }
}
</style>

